import { useMemo } from 'react';

import { useMy } from '@/hooks/useMy';

/**
 * Fetches the most recent critical demonstrated or resolved risk "First Seen" date per customer.
 *
 * Uses the `created` timestamp (when vulnerability was first discovered) as the "Last Compromise" date,
 * NOT the `updated` timestamp (which changes whenever risk status is modified).
 *
 * @returns dataMap: Map<email, ISO_timestamp> for O(1) lookup per customer.
 *          The timestamp represents the most recent `created` (First Seen) date across all critical risks.
 */
export function useLastCompromiseDate() {
  const query = useMy(
    {
      resource: 'risk',
      query: {
        node: {
          labels: ['Risk'],
          filters: [
            {
              field: 'status',
              operator: 'IN',
              value: [['OC', 'RC']],
            },
          ],
        },
        limit: 10000, // Max backend limit — prevents pagination truncating results
      },
      allTenants: true,
    },
    {
      doNotImpersonate: true,
    }
  );

  const dataMap = useMemo(() => {
    const map = new Map<string, string>();

    for (const risk of query.data ?? []) {
      const email = risk.username;
      const timestamp = risk.created;
      if (!timestamp) continue;

      const existing = map.get(email);
      if (!existing || timestamp > existing) {
        map.set(email, timestamp);
      }
    }
    return map;
  }, [query.data]);

  return {
    ...query,
    dataMap,
  };
}
